import {Component, EventEmitter, Input, Output} from "@angular/core";
import {ConfigLevel} from "../../../services/dtos/interfaces/config-level";
import {TreeNode} from "../../../services/dtos/interfaces/tree-node";
import {ApplicationDto} from "../../../services/dtos/application.dto";
import {DashboardTerminalDto} from "../../../services/dtos/dashboard-terminal.dto";
import {ConfigLevelType} from "../../../services/dtos/enums/config-level-type.enum";
import * as semver from "semver";
import {DeploymentDto} from "../../../services/dtos/deployment.dto";
import {UtilitiesService} from "../../../services/utilities.service";
import {
    DeploymentConfirmationModalComponent
} from "../../modals/deployment-confirmation/deployment-confirmation.modal.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {SmartLibraryApplication} from "../../../services/dtos/enums/smart-library-application.enum";
import {
    UninstallServicesConfirmationModalComponent
} from "../../modals/uninstall-services-confirmation/uninstall-services-confirmation.modal.component";
import {CloudApiService} from "../../../services/cloud-api.service";
import {InstallType} from "../../../services/dtos/enums/install-type.enum";
import {DeploymentUtilitiesService} from "../../../services/deployment-utilities.service";
import {RootDeploymentNode} from "./root-deployment-node";
import {ConfigDto} from "../../../services/dtos/config.dto";
import {BranchDto} from "../../../services/dtos/branch.dto";
import {FormControl} from "@angular/forms";

@Component({
    selector: 'deployment-tree',
    template: require('./deployment-tree.component.html'),
    styles: [ require('./deployment-tree.component.css') ]
})
export class DeploymentTreeComponent {
    @Input() rootNode: TreeNode;
    @Input() parentTree: DeploymentTreeComponent;

    @Input() useSpecificDateTime: boolean;
    @Input() specificDateTime: string;

    @Input() targetVersion: string;
    @Input() restartAfterUpdate: boolean;

    @Input() pendingDeployments: DeploymentDto[] = [];
    @Input() recentDeployments: DeploymentDto[] = [];

    @Input() isCompact: boolean;
    @Input() installType: InstallType;

    @Input() allTerminals: DashboardTerminalDto[] = [];

    @Output() onLoadingStarted: EventEmitter<any> = new EventEmitter();
    @Output() onErrorMessage: EventEmitter<string> = new EventEmitter<string>();
    @Output() onReloadRequested: EventEmitter<any> = new EventEmitter();

    applicationsToAdd: FormControl = new FormControl();

    constructor(
        public utilitiesService: UtilitiesService,
        public deploymentUtilitiesService: DeploymentUtilitiesService,
        private cloudApiService: CloudApiService,
        private modalService: NgbModal,
    ) {
    }

    getChildren(node: TreeNode, canUseCloudDeploymentsOnly: boolean, installType: InstallType = null) : TreeNode[] {
        if (this.isCompact && installType != null)
            return node.getChildren().filter(
                x => this.deploymentUtilitiesService.getApplicationsForNode(x, canUseCloudDeploymentsOnly, installType, this.targetVersion).length > 0);

        return node.getChildren();
    }

    getRowClass(node: TreeNode) : string {
        let classes: string[] = [];

        if (!this.isCompact) {
            classes.push("row");

            if (this.deploymentUtilitiesService.isConfigLevel(node)) {
                classes.push("clickable-row");
            }
        }

        return classes.join(' ');
    }

    getIconClass(levelItem: ConfigLevel) : string {
        let classes: string[] = [];

        if (levelItem.configLevel == ConfigLevelType.Config)
            classes.push("fa-file-text-o");
        else if (levelItem.configLevel == ConfigLevelType.Branch)
            classes.push("fa-bank");
        else if (levelItem.configLevel == ConfigLevelType.Terminal)
            classes.push("fa-desktop");

        if (!this.isCompact)
            classes.push("level-icon")
        else
            classes.push("level-icon-compact")

        return classes.join(' ');
    }

    getIconStyle(levelItem: ConfigLevel): {[key: string]: string} {
        if (!this.isCompact) {
            if (levelItem.configLevel == ConfigLevelType.Config)
                return {"padding-left": "2px"};

            if (levelItem.configLevel == ConfigLevelType.Terminal && !this.isCompact)
                return {"margin-top": "4px"};
        }

        return null;
    }

    getVersionString(node: TreeNode) : string {
        if (this.deploymentUtilitiesService.isConfigLevel(node)) {
            if (this.isCompact) return;

            let level = this.deploymentUtilitiesService.nodeAsLevel(node);

            let defaultMessage = "no applications found";
            let minVersion = level.minVersion;
            let maxVersion = level.maxVersion;

            if (this.utilitiesService.isEmptyOrSpaces(minVersion) || this.utilitiesService.isEmptyOrSpaces(maxVersion)) return defaultMessage;

            let minParsed = semver.valid(semver.coerce(minVersion));
            let maxParsed = semver.valid(semver.coerce(maxVersion));

            if (minParsed == null || maxParsed == null) return defaultMessage;

            if (semver.eq(minParsed, maxParsed))
                return `v${minVersion}`;

            return `v${minVersion} to v${maxVersion}`
        }

        return `v${this.deploymentUtilitiesService.nodeAsApplication(node).version}`;
    }

    getDeploymentStatusTitle(node: TreeNode) : string {
        if (this.deploymentUtilitiesService.isConfigLevel(node)) {
            let applicationCount = this.deploymentUtilitiesService.getApplicationsForNode(node, false, null, this.targetVersion).length;
            return `${applicationCount} application${applicationCount == 1 ? '' : 's'} installed`;
        }

        let relatedPendingDeployments = this.deploymentUtilitiesService.getDeploymentsForNode(node, this.pendingDeployments, this.targetVersion);
        if (relatedPendingDeployments.length > 0) {
            if (relatedPendingDeployments[0].version == null) return `Uninstall queued`;
            return `Update queued`;
        }

        return "No deployments queued";
    }

    getDeploymentStatus(node: TreeNode) : string {
        let relatedPendingDeployments = this.deploymentUtilitiesService.getDeploymentsForNode(node, this.pendingDeployments, this.targetVersion);

        if (this.deploymentUtilitiesService.isConfigLevel(node)) {
            return `${relatedPendingDeployments.length} deployment${relatedPendingDeployments.length == 1 ? '' : 's'} pending`
        }

        if (relatedPendingDeployments.length > 0) {
            let deployment = relatedPendingDeployments[0];
            let readableDate = new Date(deployment.requestedDateUtc + "Z").toLocaleString();

            if (deployment.version == null) return `Uninstall queued for ${readableDate}`;
            return `Update to v${deployment.version} queued for ${readableDate}`;
        }

        let relatedRecentDeployments = this.deploymentUtilitiesService.getDeploymentsForNode(node, this.recentDeployments, this.targetVersion);
        if (relatedRecentDeployments.length > 0) {
            let deployment = relatedRecentDeployments[0];
            let readableDate = new Date(deployment.appliedDateUtc + "Z").toLocaleString();

            return `Last deployment completed ${readableDate}`;
        }

        return "No recent deployments";
    }

    getDeploymentButtonTitle(title: string) : string {
        return !this.deploymentUtilitiesService.nodeAsTerminal(this.rootNode).canUseCloudDeployments ? 'Services must be v3.42.0 or later to use Cloud Deployments' : title;
    }

    getAccordionVisibility(configLevel: ConfigLevelType) : string {
        return !this.isCompact && configLevel >= ConfigLevelType.Branch ? "hidden" : null;
    }

    toggleAccordionContents(event: MouseEvent, accordion: HTMLDivElement): void {
        accordion.hidden = !accordion.hidden;

        let caret = (<HTMLDivElement>event.currentTarget).querySelector('.accordion-caret');
        if (accordion.hidden) {
            caret.classList.remove('fa-caret-up');
            caret.classList.add('fa-caret-down');
        } else {
            caret.classList.remove('fa-caret-down');
            caret.classList.add('fa-caret-up');
        }
    }

    updateChildren(event: MouseEvent, node: TreeNode) : void {
        event.stopPropagation();

        let modal = this.modalService.open(DeploymentConfirmationModalComponent);
        modal.componentInstance.title = "Batch Deployment Confirmation";
        modal.componentInstance.levelName = this.deploymentUtilitiesService.getNodeName(node);
        modal.componentInstance.version = this.targetVersion;
        modal.componentInstance.currentDeployments = this.deploymentUtilitiesService.getDeploymentsForNode(node, this.pendingDeployments, this.targetVersion).length;

        // create a deployment tree which only contains the relevant configs, branches, and terminals
        let modalRootNode = new RootDeploymentNode();
        let parentTree: DeploymentTreeComponent = this;

        let nextLevel: ConfigLevel = null;
        let previousLevel: ConfigLevel = null;

        while (node != null && parentTree != null) {
            previousLevel = nextLevel;

            if (node.configLevel == ConfigLevelType.Config) {
                nextLevel = new ConfigDto().fromObject(node);
            }

            if (node.configLevel == ConfigLevelType.Branch) {
                nextLevel = new BranchDto().fromObject(node);
            }

            if (node.configLevel == ConfigLevelType.Terminal) {
                nextLevel = new DashboardTerminalDto().fromObject(node);
            }

            if (previousLevel != null)
                nextLevel.setChildren([previousLevel]);

            node = parentTree.rootNode;
            parentTree = parentTree.parentTree;
        }

        modalRootNode.setChildren([nextLevel]);
        modal.componentInstance.rootNode = modalRootNode;

        modal.result.then((result) => {
            this.updateApplications(result);
        }).catch(() => {});
    }

    updateApplication(applicationDto: ApplicationDto, isUninstall: boolean = false) {
        this.onLoadingStarted.emit();

        let terminalId = applicationDto.terminalId;
        let application = applicationDto.application;

        let updateRequest = this.createUpdateRequest(terminalId, application, isUninstall);
        if (updateRequest == null) return;

        this.cloudApiService.updateApplication(updateRequest).subscribe(
            async result => {
                if (result.id < 1)
                    this.onErrorMessage.emit(`Failed to schedule deployment for ${this.utilitiesService.getApplicationName(result.application)}`)

                this.onReloadRequested.emit();
            }, async error => {
                this.onErrorMessage.emit(`An unexpected error occurred: ${error}`)
                this.onReloadRequested.emit();
            }
        )
    }

    updateApplications(applications: ApplicationDto[]) {
        this.onLoadingStarted.emit();

        let updateRequests = [];
        for (let application of applications) {
            let updateRequest = this.createUpdateRequest(application.terminalId, application.application);
            if (updateRequest != null) {
                updateRequests.push(updateRequest);
            } else {
                return;
            }
        }

        this.cloudApiService.updateApplications(updateRequests).subscribe(
            async result => {
                if (result.some(x => x.id < 1)) {
                    let failedDeployments = result.filter(x => x.id < 1);
                    let errorMessage = failedDeployments.map(x => {
                        let terminal = this.allTerminals.find(y => y.id == x.terminalId);
                        let terminalName = terminal == null ? `could not find name for terminal ID: ${x.terminalId}` : terminal.name;
                        return `${this.utilitiesService.getApplicationName(x.application)} (${terminalName})`;
                    }).join(", ");
                    this.onErrorMessage.emit(`Failed to schedule the following deployments: ${errorMessage}`);
                }

                this.onReloadRequested.emit();
            }, async error => {
                this.onErrorMessage.emit(`An unexpected error occurred: ${error}`)
                this.onReloadRequested.emit();
            }
        )
    }

    uninstallApplication(applicationDto: ApplicationDto) {
        // if uninstalling services, warn that Cloud Deployments will then be unusable for this terminal
        if (applicationDto.application === SmartLibraryApplication.Services) {
            let modal = this.modalService.open(UninstallServicesConfirmationModalComponent);
            modal.componentInstance.terminalName = this.deploymentUtilitiesService.nodeAsTerminal(this.rootNode).name;
            modal.result.then(_ => {
                this.updateApplication(applicationDto, true);
            }).catch(() => {});
        } else {
            this.updateApplication(applicationDto, true);
        }
    }

    cancelDeployment(application: ApplicationDto) {
        this.onLoadingStarted.emit();

        this.cloudApiService.cancelDeployment(application).subscribe(
            async result => {
                if (!result)
                    this.onErrorMessage.emit("Failed to cancel deployment.")

                this.onReloadRequested.emit();
            }, async error => {
                this.onErrorMessage.emit(`An unexpected error occurred: ${error}`)
                this.onReloadRequested.emit();
            }
        )
    }

    displayNewInstallRow(node: TreeNode): boolean {
        let installableApplications = this.getInstallableApplications(node);
        return !this.isCompact && node.configLevel == ConfigLevelType.Terminal && this.deploymentUtilitiesService.nodeAsTerminal(node).canUseCloudDeployments && installableApplications && installableApplications.length > 0;
    }

    isInstallNewApplicationsDisabled(node: TreeNode): boolean {
        let installableApplications = this.getInstallableApplications(node);
        return !(Array.isArray(this.applicationsToAdd.value) && this.applicationsToAdd.value.length > 0 && this.applicationsToAdd.value.every(x => installableApplications.indexOf(x) > -1));
    }

    getInstallNewApplicationsButtonTitle() : string {
        if (!this.applicationsToAdd.value)
            return "No applications selected";

        if (this.applicationsToAdd.value.length == 1)
            return "Install " + this.utilitiesService.getApplicationName(this.applicationsToAdd.value[0])

        return "Install " + this.utilitiesService.getApplicationNames(this.applicationsToAdd.value.slice(0, -1)).join(', ') + ' and ' + this.utilitiesService.getApplicationNames(this.applicationsToAdd.value).slice(-1)
    }

    installNewApplications(node: TreeNode): void {
        if (node.configLevel != ConfigLevelType.Terminal)
            return;

        let terminal = this.deploymentUtilitiesService.nodeAsTerminal(node);
        let applications = (this.applicationsToAdd.value as SmartLibraryApplication[]).map(x => {
            let result = new ApplicationDto();
            result.application = x;
            result.terminalId = terminal.id;
            return result;
        });

        this.applicationsToAdd.setValue(null);

        this.onLoadingStarted.emit();
        this.cloudApiService.installNewApplications(applications).subscribe(
            async result => {
                if (result.some(x => x.id < 1)) {
                    this.onErrorMessage.emit(`The following applications failed to install: ${this.utilitiesService.getApplicationNames(result.filter(x => x.id < 1).map(x => x.application)).join(", ")}`)
                    this.onReloadRequested.emit();
                }

                this.updateApplications(result.filter(x => x.id > 0))
            }, async error => {
                this.onErrorMessage.emit(`An unexpected error occurred: ${error}`)
                this.onReloadRequested.emit();
            }
        )
    }

    getInstallableApplications(node: TreeNode): SmartLibraryApplication[] {
        if (node.configLevel != ConfigLevelType.Terminal)
            return [];

        let terminal = this.deploymentUtilitiesService.nodeAsTerminal(node);
        if (DeploymentTreeComponent._whitelistedApplications != null)
            return DeploymentTreeComponent._whitelistedApplications.filter(x => !terminal.applications.some(y => y.application == x));

        this.cloudApiService.getWhitelistedApplications().subscribe(
            async result => {
                DeploymentTreeComponent._whitelistedApplications = result;
                return result.filter(x => !terminal.applications.some(y => y.application == x));
            }, async error => {
                this.onErrorMessage.emit(`An unexpected error occurred: ${error}`)
                this.onReloadRequested.emit();
            }
        )
    }

    private static _whitelistedApplications: SmartLibraryApplication[];

    private createUpdateRequest(terminalId: number, application: SmartLibraryApplication, isUninstall: boolean = false) : DeploymentDto {
        let updateRequest = new DeploymentDto();
        updateRequest.terminalId = terminalId;
        updateRequest.application = application;
        updateRequest.requestedDateUtc = new Date();
        updateRequest.restartAfterUpdate = this.restartAfterUpdate;

        if (!isUninstall)
            updateRequest.version = this.targetVersion

        if (this.useSpecificDateTime) {
            if (this.utilitiesService.isEmptyOrSpaces(this.specificDateTime)) {
                this.onErrorMessage.emit("Please enter a valid deployment time.");
                return null;
            }

            let specificDateTime = new Date(this.specificDateTime);
            if (specificDateTime > updateRequest.requestedDateUtc)
                updateRequest.requestedDateUtc = specificDateTime;
        }

        return updateRequest;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/deployment/deployment-tree/deployment-tree.component.ts